import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import { workoutAPI } from '../services/workoutAPI';

function ProgramPrintPreview({ program, onClose }) {
  const [enrichedWorkouts, setEnrichedWorkouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [includeExerciseNotes, setIncludeExerciseNotes] = useState(true);

  useEffect(() => {
    if (program && program.workouts) {
      fetchTemplateData();
    }
  }, [program]);

  const fetchTemplateData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const workoutsWithExercises = await Promise.all(
        program.workouts.map(async (workout) => {
          try {
            const template = await workoutAPI.fetchTemplateById(workout.template_id);
            return {
              ...workout,
              exercises: template?.exercises || []
              // template_notes already exists from the database JOIN - don't overwrite it
            };
          } catch (err) {
            console.error(`Error fetching template ${workout.template_id}:`, err);
            return {
              ...workout,
              exercises: []
            };
          }
        })
      );
      setEnrichedWorkouts(workoutsWithExercises);
    } catch (err) {
      console.error('Error fetching template data:', err);
      setError('Failed to load workout details');
    } finally {
      setIsLoading(false);
    }
  };

  if (!program) return null;

  // Group workouts by phase and week
  const groupWorkouts = (workouts) => {
    const grouped = {};

    workouts.forEach(workout => {
      const phase = workout.phase || 1;
      const week = workout.week || 1;
      const day = workout.day || 1;

      if (!grouped[phase]) {
        grouped[phase] = {};
      }
      if (!grouped[phase][week]) {
        grouped[phase][week] = [];
      }
      grouped[phase][week].push({ ...workout, dayNum: day });
    });

    // Sort workouts within each week by day
    Object.values(grouped).forEach(phase => {
      Object.values(phase).forEach(week => {
        week.sort((a, b) => a.dayNum - b.dayNum);
      });
    });

    return grouped;
  };

  // Format time from seconds to MM:SS
  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format exercise details based on type
  const formatExerciseDetails = (exercise) => {
    const type = exercise.exercise_type;

    if (type === 'weight_reps') {
      return formatWeightRepsExercise(exercise);
    } else if (type === 'weight_time') {
      return formatWeightTimeExercise(exercise);
    } else if (type === 'cardio_basic') {
      return formatCardioBasicExercise(exercise);
    } else if (type === 'cardio_segments') {
      return formatCardioSegmentsExercise(exercise);
    } else if (type === 'stretch') {
      return formatStretchExercise(exercise);
    } else if (type === 'rest_block') {
      return formatRestBlock(exercise);
    }

    return '';
  };

  const formatWeightRepsExercise = (exercise) => {
    const sets = exercise.sets || [];
    if (sets.length === 0) return 'No sets defined';

    const allSameWeight = sets.every(s => s.weight === sets[0].weight && s.weight_unit === sets[0].weight_unit);
    const allSameReps = sets.every(s =>
      (s.reps_prescription || s.reps) === (sets[0].reps_prescription || sets[0].reps)
    );

    if (allSameWeight && allSameReps) {
      const weight = sets[0].weight ? `@ ${sets[0].weight} ${sets[0].weight_unit}` : '';
      const reps = sets[0].reps_prescription || sets[0].reps || '?';
      return `${sets.length}x${reps} ${weight}`.trim();
    }

    const setDetails = sets.map(s => {
      const reps = s.reps_prescription || s.reps || '?';
      const weight = s.weight ? `${s.weight}${s.weight_unit}` : '';
      return weight ? `${reps}@${weight}` : reps;
    }).join(', ');

    return `${sets.length} sets: ${setDetails}`;
  };

  const formatWeightTimeExercise = (exercise) => {
    const sets = exercise.sets || [];
    if (sets.length === 0) return 'No sets defined';

    const allSameWeight = sets.every(s => s.weight === sets[0].weight && s.weight_unit === sets[0].weight_unit);
    const allSameDuration = sets.every(s => s.duration_seconds === sets[0].duration_seconds);

    if (allSameWeight && allSameDuration) {
      const weight = sets[0].weight ? `@ ${sets[0].weight} ${sets[0].weight_unit}` : '';
      const duration = sets[0].duration_seconds ? `${sets[0].duration_seconds}s` : '?';
      return `${sets.length}x${duration} ${weight}`.trim();
    }

    const setDetails = sets.map(s => {
      const duration = s.duration_seconds ? `${s.duration_seconds}s` : '?';
      const weight = s.weight ? `@${s.weight}${s.weight_unit}` : '';
      return `${duration}${weight}`;
    }).join(', ');

    return `${sets.length} sets: ${setDetails}`;
  };

  // A stretch prescribes any combination of hold, reps, weight and band, so the
  // line is assembled from whichever the template actually specified rather
  // than a fixed template like the two formatters above.
  const formatStretchExercise = (exercise) => {
    const sets = exercise.sets || [];
    if (sets.length === 0) return 'No sets defined';

    const describe = (s) => {
      const parts = [];
      if (s.duration_seconds) parts.push(`${s.duration_seconds}s`);
      if (s.reps_prescription || s.reps) parts.push(`x${s.reps_prescription || s.reps}`);
      if (s.weight) parts.push(`@${s.weight}${s.weight_unit || ''}`);
      if (s.resistance) parts.push(`(${s.resistance})`);
      return parts.join(' ') || '?';
    };

    const first = describe(sets[0]);
    if (sets.every(s => describe(s) === first)) {
      return `${sets.length}x ${first}`;
    }
    return `${sets.length} sets: ${sets.map(describe).join(', ')}`;
  };

  const formatCardioBasicExercise = (exercise) => {
    const data = exercise.cardio_data || {};
    const parts = [];

    if (data.distance) {
      parts.push(`${data.distance} ${data.distance_unit || 'miles'}`);
    }
    if (data.duration_seconds) {
      const mins = Math.floor(data.duration_seconds / 60);
      parts.push(`${mins} min`);
    }

    return parts.length > 0 ? parts.join(', ') : 'No details';
  };

  const formatCardioSegmentsExercise = (exercise) => {
    const segments = exercise.cardio_segments || [];
    if (segments.length === 0) return 'No segments defined';

    const totalDistance = segments.reduce((sum, seg) => sum + (parseFloat(seg.distance) || 0), 0);
    const unit = segments[0]?.distance_unit || 'miles';

    return `${segments.length} segments, ${totalDistance.toFixed(2)} ${unit}`;
  };

  const formatRestBlock = (exercise) => {
    const duration = exercise.rest_duration_seconds || 0;
    return `Rest: ${formatTime(duration)}`;
  };

  const isRestBlock = (exercise) => {
    return !exercise.exercise_id || exercise.exercise_type === 'rest_block';
  };

  // Generate Word document
  const generateWordDocument = async () => {
    setIsExporting(true);

    try {
      const groupedWorkouts = groupWorkouts(enrichedWorkouts);
      const phases = Object.keys(groupedWorkouts).sort((a, b) => a - b);
      const hasMultiplePhases = phases.length > 1;

      const children = [];

      // Title
      children.push(
        new Paragraph({
          text: program.name,
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 }
        })
      );

      // Description
      if (program.description) {
        children.push(
          new Paragraph({
            text: program.description,
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 }
          })
        );
      }

      // Program notes - split by newlines to preserve paragraphs
      if (program.notes) {
        const noteLines = program.notes.split(/\n+/).filter(line => line.trim());

        // Notes header
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: 'Notes:', bold: true })
            ],
            spacing: { after: 100 }
          })
        );

        // Each paragraph of notes
        noteLines.forEach((line, index) => {
          children.push(
            new Paragraph({
              text: line.trim(),
              spacing: { after: index === noteLines.length - 1 ? 300 : 100 }
            })
          );
        });
      }

      // Divider line
      children.push(
        new Paragraph({
          text: '',
          border: {
            bottom: { color: 'auto', space: 1, style: BorderStyle.SINGLE, size: 6 }
          },
          spacing: { after: 300 }
        })
      );

      // Process each phase
      phases.forEach(phase => {
        const weeks = Object.keys(groupedWorkouts[phase]).sort((a, b) => a - b);

        if (hasMultiplePhases) {
          children.push(
            new Paragraph({
              text: `Phase ${phase}`,
              heading: HeadingLevel.HEADING_1,
              spacing: { before: 400, after: 200 }
            })
          );
        }

        weeks.forEach(week => {
          const workouts = groupedWorkouts[phase][week];

          // Week header
          children.push(
            new Paragraph({
              text: `Week ${week}`,
              heading: HeadingLevel.HEADING_2,
              spacing: { before: 300, after: 150 }
            })
          );

          workouts.forEach(workout => {
            const name = workout.name || workout.template_name || 'Unnamed Workout';
            const workoutNotes = workout.notes || workout.template_notes;

            // Day + Workout name
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: `Day ${workout.dayNum}: `, bold: true }),
                  new TextRun({ text: name })
                ],
                spacing: { before: 200, after: workoutNotes ? 50 : 100 }
              })
            );

            // Workout notes - split by newlines to preserve paragraphs
            if (workoutNotes) {
              const workoutNoteLines = workoutNotes.split(/\n+/).filter(line => line.trim());
              workoutNoteLines.forEach((line, index) => {
                children.push(
                  new Paragraph({
                    children: [
                      new TextRun({ text: line.trim(), italics: true, color: '666666' })
                    ],
                    spacing: { after: index === workoutNoteLines.length - 1 ? 100 : 50 }
                  })
                );
              });
            }

            // Exercises
            const exercises = workout.exercises || [];
            if (exercises.length === 0) {
              children.push(
                new Paragraph({
                  text: '    No exercises defined',
                  spacing: { after: 100 }
                })
              );
            } else {
              // Pre-group exercises by superset_group_id, preserving order
              const supersetGroups = new Map();
              exercises.forEach(ex => {
                if (ex.superset_group_id) {
                  if (!supersetGroups.has(ex.superset_group_id)) {
                    supersetGroups.set(ex.superset_group_id, []);
                  }
                  supersetGroups.get(ex.superset_group_id).push(ex);
                }
              });

              const processedSupersets = new Set();

              exercises.forEach(exercise => {
                if (exercise.superset_group_id) {
                  // Render the whole superset the first time its group is encountered
                  if (processedSupersets.has(exercise.superset_group_id)) {
                    return;
                  }
                  processedSupersets.add(exercise.superset_group_id);

                  const supersetExercises = supersetGroups.get(exercise.superset_group_id);

                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: '    SUPERSET:', bold: true, italics: true })
                      ],
                      spacing: { before: 50 }
                    })
                  );

                  supersetExercises.forEach(ex => {
                    const exName = isRestBlock(ex) ? 'Rest' : (ex.exercise_name || 'Unknown');
                    const details = isRestBlock(ex) ? formatRestBlock(ex) : formatExerciseDetails(ex);
                    const showNotes = includeExerciseNotes && ex.notes;

                    children.push(
                      new Paragraph({
                        children: [
                          new TextRun({ text: `      - ${exName}` }),
                          details ? new TextRun({ text: ` — ${details}` }) : null
                        ].filter(Boolean),
                        spacing: { after: showNotes ? 10 : 30 }
                      })
                    );

                    // Add exercise notes if present and enabled
                    if (showNotes) {
                      children.push(
                        new Paragraph({
                          children: [
                            new TextRun({ text: `        ${ex.notes}`, italics: true, color: '666666', size: 20 })
                          ],
                          spacing: { after: 30 }
                        })
                      );
                    }
                  });
                } else {
                  // Regular exercise
                  const exName = isRestBlock(exercise) ? 'Rest' : (exercise.exercise_name || 'Unknown');
                  const details = isRestBlock(exercise) ? formatRestBlock(exercise) : formatExerciseDetails(exercise);
                  const showNotes = includeExerciseNotes && exercise.notes;

                  children.push(
                    new Paragraph({
                      children: [
                        new TextRun({ text: `    • ${exName}` }),
                        details ? new TextRun({ text: ` — ${details}` }) : null
                      ].filter(Boolean),
                      spacing: { after: showNotes ? 10 : 30 }
                    })
                  );

                  // Add exercise notes if present and enabled
                  if (showNotes) {
                    children.push(
                      new Paragraph({
                        children: [
                          new TextRun({ text: `      ${exercise.notes}`, italics: true, color: '666666', size: 20 })
                        ],
                        spacing: { after: 30 }
                      })
                    );
                  }
                }
              });
            }
          });
        });
      });

      // Create and download document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children
        }]
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `${program.name.replace(/[^a-z0-9]/gi, '_')}_program.docx`;
      saveAs(blob, fileName);

      onClose();
    } catch (err) {
      console.error('Error generating Word document:', err);
      setError('Failed to export program. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const groupedWorkouts = groupWorkouts(enrichedWorkouts);
  const phases = Object.keys(groupedWorkouts).sort((a, b) => a - b);
  const hasMultiplePhases = phases.length > 1;
  const totalWorkouts = enrichedWorkouts.length;

  return (
    <Modal show={true} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-file-export me-2"></i>
          Export Program
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="mt-2 mb-0 text-muted">Loading workout details...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        ) : (
          <div>
            <p className="mb-3">
              Export <strong>{program.name}</strong> to a Word document.
            </p>
            <div className="bg-light p-3 rounded">
              <div className="d-flex justify-content-between">
                <span className="text-muted">Workouts:</span>
                <strong>{totalWorkouts}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Weeks:</span>
                <strong>
                  {phases.reduce((total, phase) =>
                    total + Object.keys(groupedWorkouts[phase]).length, 0
                  )}
                </strong>
              </div>
              {hasMultiplePhases && (
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Phases:</span>
                  <strong>{phases.length}</strong>
                </div>
              )}
            </div>
            <Form.Check
              type="checkbox"
              id="include-exercise-notes"
              className="mt-3"
              label="Include exercise notes"
              checked={includeExerciseNotes}
              onChange={(e) => setIncludeExerciseNotes(e.target.checked)}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={generateWordDocument}
          disabled={isLoading || isExporting}
        >
          {isExporting ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Exporting...
            </>
          ) : (
            <>
              <i className="fas fa-download me-2"></i>
              Export to Word
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ProgramPrintPreview;
